import {
  CollectionResult,
  GetMenuResult,
  MediaImagesByIdsResult,
  MenuItem,
  MenuUpdateResult,
  OrderCustomerInfoResult,
  ProductAndCollectionInitResult,
  ProductAndCollectionListResult,
  ProductResult,
  ShopifyGraphqlFetchResponse,
} from "@/api/shopify-graphql/shopify-graphql.type"

const MEDIA_IMAGE_GID_PREFIX = "gid://shopify/MediaImage/"
const MEDIA_IMAGES_BY_IDS_BATCH_SIZE = 250

const MEDIA_IMAGES_BY_IDS_QUERY = `
  query mediaImagesByIds($ids: [ID!]!) {
    nodes(ids: $ids) {
      __typename
      ... on MediaImage {
        id
      }
    }
  }
`

export const shopifyGraphqlFetch = async <T>(query: string, variables?: object): Promise<ShopifyGraphqlFetchResponse<T>> => {
  const res = await fetch("shopify:admin/api/2026-07/graphql.json", {
    method: "POST",
    body: JSON.stringify({
      query,
      variables,
    }),
  })

  return {
    status: res.status,
    ...await res.json(),
  }
}

export default {
  getProduct(id: string) {
    return shopifyGraphqlFetch(
      // language=GraphQL
      `
        query GetProduct($id: ID!) {
          product(id: $id) {
            title
          }
        }`,
      { id: `gid://shopify/Product/${id}` },
    )
  },

  // 订单详情页：客户、联系方式与当前收货地址
  getOrderCustomerInfo(orderId: string | number) {
    return shopifyGraphqlFetch<OrderCustomerInfoResult>(
      // language=GraphQL
      `
        query orderCustomerInfo($id: ID!) {
          order(id: $id) {
            name
            phone
            email
            customer {
              id
              displayName
            }
            shippingAddress {
              name
              firstName
              lastName
              company
              address1
              address2
              city
              provinceCode
              zip
              countryCodeV2
              phone
              formatted
            }
          }
        }`,
      { id: `gid://shopify/Order/${orderId}` },
    )
  },

  getPublishedCheckoutProfile() {
    return shopifyGraphqlFetch(
      // language=GraphQL
      `query checkoutProfiles {
        checkoutProfiles(first: 1, query: "is_published:true") {
          edges {
            node {
              id
              name
              isPublished
              typOspPagesActive
            }
          }
        }
      }`,
    )
  },

  searchProducts(query?: string, after: string|null = null) {
    return shopifyGraphqlFetch<ProductResult>(
      `
      query searchProducts($query: String!, $after: String) {
        products(query: $query, first: 20, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          nodes {
            id
            title
            featuredMedia {
              __typename
              preview {
                __typename
                image {
                  url(transform: { maxWidth: 200, maxHeight: 200 })
                }
              }
            }
          }
        }
      }`,
      { query: `${query} ` + "status:ACTIVE,DRAFT", after },
    )
  },

  searchCollections(query?: string, after: string|null = null){
    return shopifyGraphqlFetch<CollectionResult>(
      `
      query searchCollections($query: String!, $after: String) {
        collections(query: $query, first: 20, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          nodes {
            id
            title
            image {
              url(transform: { maxWidth: 200, maxHeight: 200 })
            }
          }
        }
      }`,
      { query, after },
    )
  },


  // 初始化请求
  initProductsAndCollections(after: string|null = null){
    return shopifyGraphqlFetch<ProductAndCollectionInitResult>(
      `
      query searchCollections($after: String) {
        products(query: "status:ACTIVE,DRAFT", first: 20, after: $after) {
            pageInfo {
              hasNextPage
              endCursor
            }
            nodes {
              id
              title
              featuredMedia {
                __typename
                preview {
                  __typename
                  image {
                    url(transform: { maxWidth: 200, maxHeight: 200 })
                  }
                }
              }
            }
        }
        collections(first: 20, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          nodes {
            id
            title
            image {
              url(transform: { maxWidth: 200, maxHeight: 200 })
            }
          }
        }
      }`,
      { after },
    )
  },

  // GraphQL 同时搜索 Products 及 Collection 列表
  filteredProductsAndCollections(query?: string, productsAfter: string|null = null, collectionsAfter: string|null = null) {
    return shopifyGraphqlFetch(
      `
      query FilteredProductsAndCollections($query: String!, $productsAfter: String, $collectionsAfter: String) {
        filteredProducts: products(first: 250, after: $productsAfter, query: $query) {
          pageInfo {
            hasNextPage
            endCursor
            __typename
          }
          nodes {
            id
          }
        }
        filteredCollections: collections(first: 250, after: $collectionsAfter, query: $query) {
          pageInfo {
            hasNextPage
            endCursor
            __typename
          }
          nodes {
            id
          }
        }
      }`,
      { query, productsAfter, collectionsAfter },
    )
  },

  // GraphQL 同时搜索 Products 及 Collection 列表
  getProductAndCollectionList(ids?: string[]) {
    return shopifyGraphqlFetch<ProductAndCollectionListResult>(
      `query ProductAndCollectionList($ids: [ID!]!) {
                nodes(ids: $ids) {
                  ...CollectionFragment
                  ...ProductFragment
                  __typename
                }
              }
              
              fragment CollectionFragment on Collection {
                id
                title
                image {
                  url(transform: { maxWidth: 200, maxHeight: 200 })
                }
              }
              
              fragment ProductFragment on Product {
                id
                title
                status
                featuredMedia {
                  __typename
                  preview {
                    __typename
                    image {
                      url(transform: {maxWidth: 80, maxHeight: 80})
                    }
                  }
                }
              }`,
      { ids },
    )
  },


  // 获取Shopify菜单，需要有Menu权限
  getMenu(){
    return shopifyGraphqlFetch<GetMenuResult>(
      `query menus {
        menus(first: 250) {
          nodes {
            id
            handle
            title
            isDefault
            items {
              id
              resourceId
              tags
              title
              type
              url
              items {
                id
                resourceId
                tags
                title
                type
                url
                items {
                  id
                  resourceId
                  tags
                  title
                  type
                  url
                }
              }
            }
          }
        }
      }`,
    )
  },

  /**
   * 通过 nodes(ids:) 批量校验 MediaImage 是否仍在 Files 媒体库。
   * 缺失节点会返回 null，请求出错时保守视为全部存在以避免误删。
   */
  async resolveMediaImagesInFilesLibrary(gids: string[]): Promise<string[]> {
    const shopifyGids = gids.filter((gid) => gid.startsWith(MEDIA_IMAGE_GID_PREFIX))

    if (shopifyGids.length === 0) {
      return []
    }

    const present: string[] = []

    for (let i = 0; i < shopifyGids.length; i += MEDIA_IMAGES_BY_IDS_BATCH_SIZE) {
      const slice = shopifyGids.slice(i, i + MEDIA_IMAGES_BY_IDS_BATCH_SIZE)
      const res = await shopifyGraphqlFetch<MediaImagesByIdsResult>(
        MEDIA_IMAGES_BY_IDS_QUERY,
        { ids: slice },
      )

      if (res.errors?.length) {
        present.push(...slice)
        continue
      }

      for (const node of res.data?.nodes ?? []) {
        if (node && node.__typename === "MediaImage" && node.id) {
          present.push(node.id)
        }
      }
    }

    return present
  },

  setMenu(id: string, title: string, items: MenuItem[]){
    return shopifyGraphqlFetch<MenuUpdateResult>(
      `mutation UpdateMenu($id: ID!, $title: String!, $items: [MenuItemUpdateInput!]!) {
        menuUpdate(id: $id, title: $title, items: $items) {
          menu {
            id
            handle
            title
            items {
              id
              title
              tags
              url
              type
            }
          }
        }
      }`,
      { id, title, items },
    )
  },
}
